import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import TableContainer from '@mui/material/TableContainer';
import Paper from '@mui/material/Paper';
import MuiTable from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import { openFile, byteSize, Translate, TextFormat, getPaginationState, JhiPagination, JhiItemCount } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortUp, faSortDown } from '@fortawesome/free-solid-svg-icons';
import { APP_DATE_FORMAT, APP_LOCAL_DATE_FORMAT } from 'app/config/constants';
import { ASC, DESC, ITEMS_PER_PAGE, SORT } from 'app/shared/util/pagination.constants';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities } from './tour-extra-category.reducer';

export const TourExtraCategory = () => {
  const dispatch = useAppDispatch();

  const pageLocation = useLocation();
  const navigate = useNavigate();

  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getPaginationState(pageLocation, ITEMS_PER_PAGE, 'id'), pageLocation.search),
  );

  const tourExtraCategoryList = useAppSelector(state => state.tourExtraCategory.entities);
  const loading = useAppSelector(state => state.tourExtraCategory.loading);
  const totalItems = useAppSelector(state => state.tourExtraCategory.totalItems);

  const getAllEntities = () => {
    dispatch(
      getEntities({
        page: paginationState.activePage - 1,
        size: paginationState.itemsPerPage,
        sort: `${paginationState.sort},${paginationState.order}`,
      }),
    );
  };

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (pageLocation.search !== endURL) {
      navigate(`${pageLocation.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort]);

  useEffect(() => {
    const params = new URLSearchParams(pageLocation.search);
    const page = params.get('page');
    const sort = params.get(SORT);
    if (page && sort) {
      const sortSplit = sort.split(',');
      setPaginationState({
        ...paginationState,
        activePage: +page,
        sort: sortSplit[0],
        order: sortSplit[1],
      });
    }
  }, [pageLocation.search]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = currentPage =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = paginationState.sort;
    const order = paginationState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    } else {
      return order === ASC ? faSortUp : faSortDown;
    }
  };

  return (
    <div>
      <h2 id="tour-extra-category-heading" data-cy="TourExtraCategoryHeading">
        <Translate contentKey="jhipsterSampleApplicationApp.tourExtraCategory.home.title">Tour Extra Categories</Translate>
        <div className="d-flex justify-content-end">
          <Button
            className="me-2"
            onClick={handleSyncList}
            disabled={loading}
            variant="outlined"
            startIcon={<FontAwesomeIcon icon="sync" spin={loading} />}
          >
            <Translate contentKey="jhipsterSampleApplicationApp.tourExtraCategory.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Button
            component={Link}
            to="/tour-extra-category/new"
            variant="contained"
            color="primary"
            id="jh-create-entity"
            data-cy="entityCreateButton"
            startIcon={<FontAwesomeIcon icon="plus" />}
          >
            <Translate contentKey="jhipsterSampleApplicationApp.tourExtraCategory.home.createLabel">
              Create new Tour Extra Category
            </Translate>
          </Button>
        </div>
      </h2>
      <div className="table-responsive">
        {tourExtraCategoryList && tourExtraCategoryList.length > 0 ? (
          <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
            <MuiTable>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('id')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.tourExtraCategory.id">ID</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('code')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.tourExtraCategory.code">Code</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('code')} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('enabled')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.tourExtraCategory.enabled">Enabled</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('enabled')} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('icon')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.tourExtraCategory.icon">Icon</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('icon')} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('defaultImage')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.tourExtraCategory.defaultImage">Default Image</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('defaultImage')} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('defaultImageData')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.tourExtraCategory.defaultImageData">Default Image Data</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('defaultImageData')} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('shopCategoryId')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.tourExtraCategory.shopCategoryId">Shop Category Id</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('shopCategoryId')} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('shopUrl')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.tourExtraCategory.shopUrl">Shop Url</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('shopUrl')} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('createdDate')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.tourExtraCategory.createdDate">Created Date</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('createdDate')} />
                  </TableCell>
                  <TableCell>
                    <Translate contentKey="jhipsterSampleApplicationApp.tourExtraCategory.createdBy">Created By</Translate>{' '}
                    <FontAwesomeIcon icon="sort" />
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {tourExtraCategoryList.map((tourExtraCategory, i) => (
                  <TableRow key={`entity-${i}`} data-cy="entityTable">
                    <TableCell>
                      <Button component={Link} to={`/tour-extra-category/${tourExtraCategory.id}`} variant="text" size="small">
                        {tourExtraCategory.id}
                      </Button>
                    </TableCell>
                    <TableCell>{tourExtraCategory.code}</TableCell>
                    <TableCell>{tourExtraCategory.enabled ? 'true' : 'false'}</TableCell>
                    <TableCell>{tourExtraCategory.icon}</TableCell>
                    <TableCell>{tourExtraCategory.defaultImage}</TableCell>
                    <TableCell>
                      {tourExtraCategory.defaultImageData ? (
                        <div>
                          {tourExtraCategory.defaultImageDataContentType ? (
                            <a onClick={openFile(tourExtraCategory.defaultImageDataContentType, tourExtraCategory.defaultImageData)}>
                              <img
                                src={`data:${tourExtraCategory.defaultImageDataContentType};base64,${tourExtraCategory.defaultImageData}`}
                                style={{ maxHeight: '30px' }}
                              />
                              &nbsp;
                            </a>
                          ) : null}
                          <span>
                            {tourExtraCategory.defaultImageDataContentType}, {byteSize(tourExtraCategory.defaultImageData)}
                          </span>
                        </div>
                      ) : null}
                    </TableCell>
                    <TableCell>{tourExtraCategory.shopCategoryId}</TableCell>
                    <TableCell>{tourExtraCategory.shopUrl}</TableCell>
                    <TableCell>
                      {tourExtraCategory.createdDate ? (
                        <TextFormat type="date" value={tourExtraCategory.createdDate} format={APP_LOCAL_DATE_FORMAT} />
                      ) : null}
                    </TableCell>
                    <TableCell>{tourExtraCategory.createdBy ? tourExtraCategory.createdBy.login : ''}</TableCell>
                    <TableCell className="text-end">
                      <ButtonGroup variant="outlined" size="small" aria-label="entity actions">
                        <Button
                          component={Link}
                          to={`/tour-extra-category/${tourExtraCategory.id}/edit?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`}
                          color="primary"
                          startIcon={<FontAwesomeIcon icon="pencil-alt" />}
                        >
                        </Button>
                        <Button
                          onClick={() =>
                            (window.location.href = `/tour-extra-category/${tourExtraCategory.id}/delete?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`)
                          }
                          color="error"
                          startIcon={<FontAwesomeIcon icon="trash" />}
                        >
                        </Button>
                      </ButtonGroup>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </MuiTable>
          </TableContainer>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="jhipsterSampleApplicationApp.tourExtraCategory.home.notFound">
                No Tour Extra Categories found
              </Translate>
            </div>
          )
        )}
      </div>
      {totalItems ? (
        <div className={tourExtraCategoryList && tourExtraCategoryList.length > 0 ? '' : 'd-none'}>
          <div className="justify-content-center d-flex">
            <JhiItemCount page={paginationState.activePage} total={totalItems} itemsPerPage={paginationState.itemsPerPage} i18nEnabled />
          </div>
          <div className="justify-content-center d-flex">
            <JhiPagination
              activePage={paginationState.activePage}
              onSelect={handlePagination}
              maxButtons={5}
              itemsPerPage={paginationState.itemsPerPage}
              totalItems={totalItems}
            />
          </div>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default TourExtraCategory;
